import React, { Component, Fragment } from 'react';
import { compose } from 'recompose';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Formik, FormikValues, Form as FormikForm } from 'formik';
import * as Yup from 'yup';

import { withUser, withClient, IWithUser, IWithClient } from 'providers';
import {
  withClientDepartments,
  withClientLocations,
  IWithClientDepartments,
  IWithClientLocations,
} from 'providers/queries';

import { ActionContainer, SubmitButton, ValidatedSelect, ValidatedTextField } from 'components';

interface IProps {
  working: boolean;
  onSubmit: (values: FormikValues) => void;
}

interface IPropsEnhanced
  extends IProps,
    WithTranslation,
    IWithUser,
    IWithClient,
    IWithClientDepartments,
    IWithClientLocations {
  working: boolean;
  onSubmit: () => void;
}

const enhancers = compose<IPropsEnhanced, IProps>(
  withTranslation(),
  withUser,
  withClient,
  withClientDepartments,
  withClientLocations,
);

class Form extends Component<IPropsEnhanced> {
  validationSchema: Yup.ObjectSchema<{}>;

  constructor(props: IPropsEnhanced) {
    super(props);

    const { client, t } = props;
    const { locations_in_signup_required, departments_in_signup_required } = client;
    const required: string = t('common:required');

    const locationValidation = locations_in_signup_required
      ? Yup.string().required(required)
      : Yup.string().nullable(true);

    const departmentValidation = departments_in_signup_required
      ? Yup.string().required(required)
      : Yup.string().nullable(true);

    this.validationSchema = Yup.object().shape({
      full_name: Yup.string().required(required),
      job_title: Yup.string().required(required),
      location_id: locationValidation,
      department_id: departmentValidation,
    });
  }

  renderSelects() {
    const { client, clientDepartments, clientLocations } = this.props;
    const { departments_in_signup_required, locations_in_signup_required } = client;

    const hasLocations = !!clientLocations.client_locations.length;
    const hasDepartments = !!clientDepartments.client_departments.length;

    return (
      <Fragment>
        {hasLocations && (
          <ValidatedSelect
            name="location_id"
            i18nKey="user:location"
            required={locations_in_signup_required}
            items={clientLocations.client_locations}
          />
        )}

        {hasDepartments && (
          <ValidatedSelect
            name="department_id"
            i18nKey="user:department"
            required={departments_in_signup_required}
            items={clientDepartments.client_departments}
          />
        )}
      </Fragment>
    );
  }

  render() {
    const { onSubmit, t, user, working } = this.props;
    const { full_name, job_title, department_id, location_id } = user;

    const initialValues = {
      full_name: full_name || '',
      job_title: job_title || '',
      department_id,
      location_id,
    };

    return (
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={this.validationSchema}>
        {() => (
          <FormikForm>
            <ValidatedTextField name="full_name" i18nKey="user:fullName" />
            <ValidatedTextField name="job_title" i18nKey="user:title" />

            {this.renderSelects()}

            <ActionContainer>
              <SubmitButton working={working} text={t('common:next')} />
            </ActionContainer>
          </FormikForm>
        )}
      </Formik>
    );
  }
}

export default enhancers(Form);
